import { useState } from "react";
import { X, Copy, MessageCircle, Mail, Link as LinkIcon, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr } from "../lib";

/**
 * ShareDialog — WhatsApp / Email / Copy Link / Download PDF.
 * Every share event is recorded on the server via POST /estimates/:id/share.
 */
export function ShareDialog({ est, onClose, onDownload }) {
  const cust = est.customer_snapshot || {};
  const veh = est.vehicle_snapshot || {};
  const total = est.totals?.grand_total || 0;
  const defaultMsg =
    `Dear ${cust.name || "Customer"}, please find Estimate ${est.number} for your ` +
    `${[veh.make, veh.model].filter(Boolean).join(" ") || "vehicle"}. ` +
    `The estimated total is ₹${total.toLocaleString("en-IN")} and the estimate is valid until ${est.valid_until}.`;
  const [msg, setMsg] = useState(defaultMsg);
  const [to, setTo] = useState({ phone: cust.whatsapp || cust.phone || "", email: cust.email || "" });

  const record = async (channel, extra = {}) => {
    try { await api.post(`/estimates/${est.id}/share`, { channel, message: msg, ...extra }); }
    catch (_e) { /* offline-safe: recording is best-effort */ }
  };

  const sendWhatsApp = async () => {
    if (!to.phone) return toast.error("Enter a WhatsApp number");
    const phone = to.phone.replace(/[^\d]/g, "");
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
    await record("whatsapp", { to: to.phone });
    toast.success("Opened WhatsApp");
  };

  const sendEmail = async () => {
    if (!to.email) return toast.error("Enter an email address");
    const url = `mailto:${to.email}?subject=${encodeURIComponent(`Estimate ${est.number}`)}&body=${encodeURIComponent(msg)}`;
    window.location.href = url;
    await record("email", { to: to.email });
    toast.success("Opened your email app");
  };

  const copyLink = async () => {
    const link = `${window.location.origin}/estimates/${est.id}`;
    try { await navigator.clipboard.writeText(link); } catch (_e) { /* clipboard may be disabled */ }
    await record("link");
    toast.success("Link copied to clipboard");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="est-share-dialog">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-[#F1FAEE]">
          <div>
            <h3 className="text-[14px] font-semibold text-[#1d3557]">Share estimate</h3>
            <p className="text-[11.5px] text-[#667085]">Send this estimate to the customer.</p>
          </div>
          <button onClick={onClose} className="text-[#a8dadc] hover:text-[#e63946]"><X size={16} /></button>
        </header>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Message</span>
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              rows={4}
              data-testid="share-message"
              className="mt-1 w-full px-2 py-1.5 rounded border border-[#E4E7EC] text-[12.5px] focus:border-[#457b9d] outline-none resize-none"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">WhatsApp / Phone</span>
              <input
                type="text" value={to.phone}
                onChange={(e) => setTo({ ...to, phone: e.target.value })}
                data-testid="share-phone"
                className="mt-1 w-full h-9 px-2 rounded border border-[#E4E7EC] text-[13px] focus:border-[#457b9d] outline-none"
              />
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Email</span>
              <input
                type="email" value={to.email}
                onChange={(e) => setTo({ ...to, email: e.target.value })}
                data-testid="share-email"
                className="mt-1 w-full h-9 px-2 rounded border border-[#E4E7EC] text-[13px] focus:border-[#457b9d] outline-none"
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <ChannelBtn Icon={MessageCircle} label="WhatsApp" onClick={sendWhatsApp} tone="green" testId="share-whatsapp" />
            <ChannelBtn Icon={Mail} label="Email" onClick={sendEmail} tone="blue" testId="share-email-btn" />
            <ChannelBtn Icon={LinkIcon} label="Copy Link" onClick={copyLink} testId="share-link" />
            <ChannelBtn Icon={Download} label="Download PDF" onClick={() => { onDownload(); onClose(); }} testId="share-download" />
          </div>
        </div>
      </div>
    </div>
  );
}

const ChannelBtn = ({ Icon, label, onClick, tone = "default", testId }) => {
  const toneCls = {
    default: "bg-white border-[#E4E7EC] text-[#1d3557] hover:border-[#457b9d]",
    green: "bg-[#12B76A] hover:bg-[#0f9a58] text-white border-transparent",
    blue: "bg-[#457b9d] hover:bg-[#3a6b8a] text-white border-transparent",
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`h-11 rounded-lg border text-[12.5px] font-semibold inline-flex items-center justify-center gap-2 ${toneCls}`}
    >
      <Icon size={14} /> {label}
    </button>
  );
};
